import { useState, useEffect } from 'react';
import {
  Tabs,
  Table,
  TextInput,
  Select,
  Button,
  Group,
  Badge,
  Pagination,
  Card,
  Stack,
  Text,
  Loader,
  Alert,
  ActionIcon,
  Modal,
  Title,
  Textarea,
  NumberInput,
  Image,
  Grid,
  Collapse,
  ScrollArea,
  Divider,
  Box,
  Tooltip,
  Code,
  ThemeIcon
} from '@mantine/core';
import {
  IconSearch,
  IconEye,
  IconCheck,
  IconX,
  IconExclamationCircle,
  IconRefresh,
  IconBuildingStore,
  IconPhoto,
  IconChevronDown,
  IconChevronUp,
  IconShield,
  IconShieldCheck,
  IconShieldX,
  IconClock,
  IconZoomIn,
  IconDownload,
  IconAlertCircle,
  IconInfoCircle
} from '@tabler/icons-react';
import { api } from '../../services/api';

const SellerVerification = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerModalOpen, setSellerModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [commissionRate, setCommissionRate] = useState(5.0);
  const [idVerification, setIdVerification] = useState(null);
  const [idVerificationLoading, setIdVerificationLoading] = useState(false);
  const [idVerificationError, setIdVerificationError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    frontendData: false,
    serverData: false
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  useEffect(() => {
    fetchSellers();
  }, [currentPage, searchTerm, statusFilter]);

  const fetchSellers = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        status: statusFilter
      });
      
      if (searchTerm) params.append('search', searchTerm);

      const response = await api.getAdminSellers(Object.fromEntries(params));
      setSellers(response.sellers);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      console.error('Error fetching sellers:', err);
      setError(err.response?.data?.error || 'Failed to load sellers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchSellers();
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const viewSellerDetails = async (seller) => {
    setSelectedSeller(seller);
    setVerificationNotes(seller.verification_notes || '');
    setCommissionRate(seller.commission_rate || 5.0);
    setSellerModalOpen(true);
    
    // Fetch ID verification data for this seller
    await fetchSellerIdVerification(seller.id);
  };

  const fetchSellerIdVerification = async (sellerId) => {
    try {
      setIdVerificationLoading(true);
      setIdVerificationError(null);
      
      // Use admin endpoint to get seller's ID verification
      const response = await api.getAdminSellerIDVerification(sellerId);
      
      if (response.verification) {
        setIdVerification(response.verification);
        setIdVerificationError(null);
      } else {
        // Backend returned successful response but no verification data
        setIdVerification(null);
        setIdVerificationError(response.message || 'No ID verification submitted yet');
      }
    } catch (err) {
      console.error('Error fetching ID verification:', err);
      if (err.response?.status === 404) {
        setIdVerificationError('No ID verification submitted yet');
      } else {
        setIdVerificationError(err.response?.data?.error || 'Failed to load ID verification');
      }
      setIdVerification(null);
    } finally {
      setIdVerificationLoading(false);
    }
  };

  const updateIdVerificationStatus = async (sellerId, status, reason = '') => {
    try {
      setUpdating(true);
      const updateData = {
        verification_status: status,
        admin_notes: reason || verificationNotes
      };
      
      await api.patch(`/admin/princesses/${sellerId}/id-verification/`, updateData);
      
      // Refresh ID verification data
      await fetchSellerIdVerification(sellerId);
      
      // Refresh seller data
      await fetchSellers();
      
    } catch (err) {
      console.error('Error updating ID verification:', err);
      setError(err.response?.data?.error || 'Failed to update ID verification');
    } finally {
      setUpdating(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const openImageModal = (imageUrl, title) => {
    setSelectedImage({ url: imageUrl, title });
    setImageModalOpen(true);
  };

  const updateSellerStatus = async (sellerId, newStatus, isVerified = null) => {
    try {
      setUpdating(true);
      const updateData = {
        seller_status: newStatus,
        verification_notes: verificationNotes
      };
      
      if (isVerified !== null) {
        updateData.is_verified = isVerified;
      }
      
      if (commissionRate !== selectedSeller?.commission_rate) {
        updateData.commission_rate = commissionRate;
      }

      const response = await api.updateAdminSeller(sellerId, updateData);
      
      // Update the seller in the list
      setSellers(prev => prev.map(seller => 
        seller.id === sellerId 
          ? { ...seller, ...response.seller }
          : seller
      ));
      
      // Update selected seller if it's the same one
      if (selectedSeller?.id === sellerId) {
        setSelectedSeller({ ...selectedSeller, ...response.seller });
      }
      
      setSellerModalOpen(false);
    } catch (err) {
      console.error('Error updating seller:', err);
      setError(err.response?.data?.error || 'Failed to update seller');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'approved': return 'green';
      case 'pending': return 'yellow';
      case 'rejected': return 'red';
      case 'suspended': return 'orange';
      default: return 'gray';
    }
  };

  const getIdVerificationStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'green';
      case 'pending': return 'yellow';
      case 'rejected': return 'red';
      case 'submitted': return 'blue';
      case 'needs_review': return 'orange';
      default: return 'gray';
    }
  };

  const getIdVerificationIcon = (status) => {
    switch (status) {
      case 'verified': return IconShieldCheck;
      case 'pending': return IconClock;
      case 'rejected': return IconShieldX;
      case 'submitted': return IconShield;
      case 'needs_review': return IconAlertCircle;
      default: return IconInfoCircle;
    }
  };

  const formatBarcodeData = (data) => {
    if (!data || typeof data !== 'object') return null;
    
    return Object.entries(data)
      .filter(([key, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => ({
        key: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        value: String(value)
      }));
  };

  const compareDataValues = (frontendValue, serverValue) => {
    if (frontendValue === serverValue) return 'match';
    if (!frontendValue || !serverValue) return 'missing';
    return 'mismatch';
  };

  if (loading && sellers.length === 0) {
    return (
      <Tabs.Panel value="sellers" pt="xs">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Tabs.Panel>
    );
  }

  return (
    <Tabs.Panel value="sellers" pt="xs">
      <Card shadow="sm" padding="lg" radius="md" withBorder mb="md">
        <Stack gap="md">
          <Group align="center" gap="md">
            <TextInput
              placeholder="Search sellers..."
              leftSection={<IconSearch size="0.9rem" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              data={[
                { value: 'all', label: 'All Statuses' },
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'suspended', label: 'Suspended' }
              ]}
            />
            <Button onClick={handleSearch} loading={loading}>
              Search
            </Button>
            <ActionIcon onClick={fetchSellers} variant="light">
              <IconRefresh size="1rem" />
            </ActionIcon>
          </Group>

          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {error}
            </Alert>
          )}
        </Stack>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <ScrollArea>
          <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Business Name</Table.Th>
              <Table.Th>Owner</Table.Th>
              <Table.Th>Business Type</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Verified</Table.Th>
              <Table.Th>ID Status</Table.Th>
              <Table.Th>Applied</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sellers.map((seller) => (
              <Table.Tr key={seller.id}>
                <Table.Td>
                  <Group gap="xs">
                    <IconBuildingStore size="1rem" />
                    <Text fw={500}>{seller.business_name}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm" fw={500}>{seller.user.username}</Text>
                    <Text size="xs" c="dimmed">{seller.user.email}</Text>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Text transform="capitalize">
                    {seller.business_type.replace('_', ' ')}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={getStatusBadgeColor(seller.seller_status)} variant="light">
                    {seller.seller_status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={seller.is_verified ? 'green' : 'gray'} variant="light" size="sm">
                    {seller.is_verified ? 'Yes' : 'No'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge 
                    color={getIdVerificationStatusColor(seller.id_verification_status || 'none')} 
                    variant="light" 
                    size="sm"
                  >
                    {seller.id_verification_status ? 
                      seller.id_verification_status.replace('_', ' ').toUpperCase() : 
                      'Not Submitted'
                    }
                  </Badge>
                </Table.Td>
                <Table.Td>{formatDate(seller.created_at)}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <ActionIcon 
                      variant="light" 
                      onClick={() => viewSellerDetails(seller)}
                    >
                      <IconEye size="1rem" />
                    </ActionIcon>
                    {seller.seller_status === 'pending' && (
                      <>
                        <ActionIcon 
                          variant="light" 
                          color="green"
                          onClick={() => updateSellerStatus(seller.id, 'approved', true)}
                        >
                          <IconCheck size="1rem" />
                        </ActionIcon>
                        <ActionIcon 
                          variant="light" 
                          color="red"
                          onClick={() => updateSellerStatus(seller.id, 'rejected', false)}
                        >
                          <IconX size="1rem" />
                        </ActionIcon>
                      </>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </ScrollArea>

        {sellers.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Text c="dimmed">No sellers found</Text>
          </div>
        )}

        {totalPages > 1 && (
          <Group align="center" mt="md">
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
            />
          </Group>
        )}
      </Card>

      {/* Seller Details Modal */}
      <Modal
        opened={sellerModalOpen}
        onClose={() => {
          setSellerModalOpen(false);
          setIdVerification(null);
          setIdVerificationError(null);
          setExpandedSections({ frontendData: false, serverData: false });
        }}
        title={<Title order={3}>Seller Verification</Title>}
        size="lg"
      >
        {selectedSeller && (
          <Stack gap="md">
            {/* Business Information */}
            <Card withBorder p="md">
              <Title order={4} mb="md">Business Information</Title>
              <Stack gap="sm">
                <Group align="apart">
                  <Text fw={500}>Business Name:</Text>
                  <Text>{selectedSeller.business_name}</Text>
                </Group>
                <Group align="apart">
                  <Text fw={500}>Business Type:</Text>
                  <Text transform="capitalize">
                    {selectedSeller.business_type.replace('_', ' ')}
                  </Text>
                </Group>
                <Group align="apart">
                  <Text fw={500}>Business Email:</Text>
                  <Text>{selectedSeller.business_email}</Text>
                </Group>
                <Group align="apart">
                  <Text fw={500}>Business Phone:</Text>
                  <Text>{selectedSeller.business_phone}</Text>
                </Group>
                {selectedSeller.business_description && (
                  <div>
                    <Text fw={500} mb="xs">Description:</Text>
                    <Text size="sm">{selectedSeller.business_description}</Text>
                  </div>
                )}
              </Stack>
            </Card>

            {/* Address Information */}
            <Card withBorder p="md">
              <Title order={4} mb="md">Business Address</Title>
              <Text size="sm">
                {selectedSeller.business_address.line1}
                {selectedSeller.business_address.line2 && (
                  <><br />{selectedSeller.business_address.line2}</>
                )}
                <br />
                {selectedSeller.business_address.city}, {selectedSeller.business_address.state} {selectedSeller.business_address.postal_code}
                <br />
                {selectedSeller.business_address.country}
              </Text>
            </Card>

            {/* Legal Information */}
            <Card withBorder p="md">
              <Title order={4} mb="md">Legal Information</Title>
              <Stack gap="sm">
                {selectedSeller.tax_id && (
                  <Group align="apart">
                    <Text fw={500}>Tax ID:</Text>
                    <Text>{selectedSeller.tax_id}</Text>
                  </Group>
                )}
                {selectedSeller.business_license && (
                  <Group align="apart">
                    <Text fw={500}>Business License:</Text>
                    <Text>{selectedSeller.business_license}</Text>
                  </Group>
                )}
              </Stack>
            </Card>

            {/* ID Verification Section */}
            <Card withBorder p="md">
              <Group align="center" gap="md" mb="md">
                <Title order={4}>ID Verification</Title>
                {idVerificationLoading && <Loader size="sm" />}
              </Group>

              {idVerificationError && (
                <Alert icon={<IconExclamationCircle size="1rem" />} color="red" mb="md">
                  {idVerificationError}
                </Alert>
              )}

              {!idVerificationLoading && !idVerificationError && idVerification && (
                <Stack gap="md">
                  {/* Verification Status Overview */}
                  <Card withBorder p="sm" bg="gray.1">
                    <Group align="center" gap="md">
                      <ThemeIcon 
                        color={getIdVerificationStatusColor(idVerification.verification_status)}
                        variant="light"
                        size="lg"
                      >
                        {(() => {
                          const IconComponent = getIdVerificationIcon(idVerification.verification_status);
                          return <IconComponent size="1.2rem" />;
                        })()}
                      </ThemeIcon>
                      <div style={{ flex: 1 }}>
                        <Text fw={600} size="sm">
                          Status: {idVerification.verification_status?.replace('_', ' ').toUpperCase() || 'Unknown'}
                        </Text>
                        {idVerification.submitted_at && (
                          <Text size="xs" c="dimmed">
                            Submitted: {formatDate(idVerification.submitted_at)}
                          </Text>
                        )}
                        {idVerification.verified_at && (
                          <Text size="xs" c="dimmed">
                            Verified: {formatDate(idVerification.verified_at)}
                          </Text>
                        )}
                      </div>
                      {idVerification.validation_score && (
                        <Stack gap={0} align="center">
                          <Text size="lg" fw={700} c={idVerification.validation_score >= 0.8 ? 'green' : idVerification.validation_score >= 0.6 ? 'orange' : 'red'}>
                            {Math.round(idVerification.validation_score * 100)}%
                          </Text>
                          <Text size="xs" c="dimmed">Confidence</Text>
                        </Stack>
                      )}
                    </Group>
                  </Card>

                  {/* ID Images Display */}
                  {(idVerification.selfie_with_id_url || idVerification.front_of_id_url || idVerification.back_of_id_url) && (
                    <Box>
                      <Group align="center" gap="sm" mb="sm">
                        <IconPhoto size="1.2rem" />
                        <Text fw={500}>ID Documents</Text>
                      </Group>
                      <Grid>
                        {idVerification.selfie_with_id_url && (
                          <Grid.Col span={4}>
                            <Card withBorder p="xs">
                              <Stack gap="xs" align="center">
                                <Image
                                  src={idVerification.selfie_with_id_url}
                                  alt="Selfie with ID"
                                  height={120}
                                  fit="contain"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => openImageModal(idVerification.selfie_with_id_url, 'Selfie with ID')}
                                  fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' fill='%23f1f3f4'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='14' fill='%23666' text-anchor='middle' dy='0.3em'%3EImage not available%3C/text%3E%3C/svg%3E"
                                />
                                <Text size="xs" ta="center" fw={500}>Selfie with ID</Text>
                                <Group gap="xs" justify="center">
                                  <Tooltip label="View full size">
                                    <ActionIcon 
                                      size="sm" 
                                      variant="light"
                                      onClick={() => openImageModal(idVerification.selfie_with_id_url, 'Selfie with ID')}
                                    >
                                      <IconZoomIn size="0.8rem" />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Stack>
                            </Card>
                          </Grid.Col>
                        )}
                        {idVerification.front_of_id_url && (
                          <Grid.Col span={4}>
                            <Card withBorder p="xs">
                              <Stack gap="xs" align="center">
                                <Image
                                  src={idVerification.front_of_id_url}
                                  alt="Front of ID"
                                  height={120}
                                  fit="contain"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => openImageModal(idVerification.front_of_id_url, 'Front of ID')}
                                  fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' fill='%23f1f3f4'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='14' fill='%23666' text-anchor='middle' dy='0.3em'%3EImage not available%3C/text%3E%3C/svg%3E"
                                />
                                <Text size="xs" ta="center" fw={500}>Front of ID</Text>
                                <Group gap="xs" justify="center">
                                  <Tooltip label="View full size">
                                    <ActionIcon 
                                      size="sm" 
                                      variant="light"
                                      onClick={() => openImageModal(idVerification.front_of_id_url, 'Front of ID')}
                                    >
                                      <IconZoomIn size="0.8rem" />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Stack>
                            </Card>
                          </Grid.Col>
                        )}
                        {idVerification.back_of_id_url && (
                          <Grid.Col span={4}>
                            <Card withBorder p="xs">
                              <Stack gap="xs" align="center">
                                <Image
                                  src={idVerification.back_of_id_url}
                                  alt="Back of ID"
                                  height={120}
                                  fit="contain"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => openImageModal(idVerification.back_of_id_url, 'Back of ID')}
                                  fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' fill='%23f1f3f4'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='14' fill='%23666' text-anchor='middle' dy='0.3em'%3EImage not available%3C/text%3E%3C/svg%3E"
                                />
                                <Text size="xs" ta="center" fw={500}>Back of ID</Text>
                                <Group gap="xs" justify="center">
                                  <Tooltip label="View full size">
                                    <ActionIcon 
                                      size="sm" 
                                      variant="light"
                                      onClick={() => openImageModal(idVerification.back_of_id_url, 'Back of ID')}
                                    >
                                      <IconZoomIn size="0.8rem" />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Stack>
                            </Card>
                          </Grid.Col>
                        )}
                      </Grid>
                    </Box>
                  )}

                  {/* Barcode Data Display */}
                  {(idVerification.frontend_barcode_data || idVerification.server_barcode_data) && (
                    <Box>
                      <Text fw={500} mb="sm">Barcode Data Analysis</Text>
                      
                      {/* Frontend Barcode Data */}
                      {idVerification.frontend_barcode_data && (
                        <Card withBorder p="sm" mb="sm">
                          <Group 
                            align="center" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleSection('frontendData')}
                          >
                            <Text fw={500} size="sm" style={{ flex: 1 }}>
                              Frontend Parsed Data
                            </Text>
                            {expandedSections.frontendData ? 
                              <IconChevronUp size="1rem" /> : 
                              <IconChevronDown size="1rem" />
                            }
                          </Group>
                          <Collapse in={expandedSections.frontendData}>
                            <Stack gap="xs" mt="sm">
                              {formatBarcodeData(idVerification.frontend_barcode_data)?.map(({ key, value }) => (
                                <Group key={key} align="apart">
                                  <Text size="sm" fw={500}>{key}:</Text>
                                  <Text size="sm" style={{ maxWidth: '60%' }} truncate>{value}</Text>
                                </Group>
                              ))}
                            </Stack>
                          </Collapse>
                        </Card>
                      )}

                      {/* Server Barcode Data */}
                      {idVerification.server_barcode_data && (
                        <Card withBorder p="sm" mb="sm">
                          <Group 
                            align="center" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleSection('serverData')}
                          >
                            <Text fw={500} size="sm" style={{ flex: 1 }}>
                              Server Processed Data
                            </Text>
                            {expandedSections.serverData ? 
                              <IconChevronUp size="1rem" /> : 
                              <IconChevronDown size="1rem" />
                            }
                          </Group>
                          <Collapse in={expandedSections.serverData}>
                            <Stack gap="xs" mt="sm">
                              {formatBarcodeData(idVerification.server_barcode_data)?.map(({ key, value }) => (
                                <Group key={key} align="apart">
                                  <Text size="sm" fw={500}>{key}:</Text>
                                  <Text size="sm" style={{ maxWidth: '60%' }} truncate>{value}</Text>
                                </Group>
                              ))}
                            </Stack>
                          </Collapse>
                        </Card>
                      )}

                      {/* Data Comparison */}
                      {idVerification.frontend_barcode_data && idVerification.server_barcode_data && (
                        <Card withBorder p="sm" bg="blue.0">
                          <Text fw={500} size="sm" mb="sm">Data Comparison</Text>
                          <Stack gap="xs">
                            {Object.keys(idVerification.frontend_barcode_data).map(key => {
                              const frontendValue = idVerification.frontend_barcode_data[key];
                              const serverValue = idVerification.server_barcode_data[key];
                              const comparison = compareDataValues(frontendValue, serverValue);
                              
                              return (
                                <Group key={key} align="center">
                                  <Text size="sm" fw={500} style={{ minWidth: '120px' }}>
                                    {key.replace(/_/g, ' ')}:
                                  </Text>
                                  <Badge 
                                    size="sm" 
                                    color={comparison === 'match' ? 'green' : comparison === 'mismatch' ? 'red' : 'gray'}
                                    variant="light"
                                  >
                                    {comparison}
                                  </Badge>
                                  {comparison === 'mismatch' && (
                                    <Tooltip 
                                      label={`Frontend: "${frontendValue}" vs Server: "${serverValue}"`}
                                      multiline
                                      width={300}
                                    >
                                      <IconInfoCircle size="1rem" color="orange" />
                                    </Tooltip>
                                  )}
                                </Group>
                              );
                            })}
                          </Stack>
                        </Card>
                      )}
                    </Box>
                  )}

                  {/* Admin Notes */}
                  {idVerification.admin_notes && (
                    <Card withBorder p="sm" bg="yellow.0">
                      <Text fw={500} size="sm" mb="xs">Admin Notes</Text>
                      <Text size="sm">{idVerification.admin_notes}</Text>
                    </Card>
                  )}

                  {/* ID Verification Admin Actions */}
                  {idVerification.verification_status !== 'verified' && (
                    <Card withBorder p="sm" bg="gray.0">
                      <Text fw={500} size="sm" mb="sm">ID Verification Actions</Text>
                      <Group>
                        <Button
                          size="sm"
                          color="green"
                          leftSection={<IconShieldCheck size="1rem" />}
                          onClick={() => updateIdVerificationStatus(selectedSeller.id, 'verified')}
                          loading={updating}
                        >
                          Approve ID
                        </Button>
                        <Button
                          size="sm"
                          color="red"
                          leftSection={<IconShieldX size="1rem" />}
                          onClick={() => updateIdVerificationStatus(selectedSeller.id, 'rejected')}
                          loading={updating}
                        >
                          Reject ID
                        </Button>
                        <Button
                          size="sm"
                          color="orange"
                          leftSection={<IconAlertCircle size="1rem" />}
                          onClick={() => updateIdVerificationStatus(selectedSeller.id, 'needs_review')}
                          loading={updating}
                        >
                          Needs Review
                        </Button>
                      </Group>
                    </Card>
                  )}
                </Stack>
              )}

              {!idVerificationLoading && !idVerification && !idVerificationError && (
                <Alert icon={<IconInfoCircle size="1rem" />} color="blue">
                  No ID verification has been submitted by this seller yet.
                </Alert>
              )}
            </Card>

            {/* Metrics */}
            <Card withBorder p="md">
              <Title order={4} mb="md">Performance Metrics</Title>
              <Group grow>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Total Sales</Text>
                  <Text size="lg" fw={700}>${selectedSeller.metrics.total_sales}</Text>
                </Stack>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Total Orders</Text>
                  <Text size="lg" fw={700}>{selectedSeller.metrics.total_orders}</Text>
                </Stack>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Average Rating</Text>
                  <Text size="lg" fw={700}>{selectedSeller.metrics.average_rating}/5</Text>
                </Stack>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Total Reviews</Text>
                  <Text size="lg" fw={700}>{selectedSeller.metrics.total_reviews}</Text>
                </Stack>
              </Group>
            </Card>

            {/* Admin Actions */}
            <Card withBorder p="md">
              <Title order={4} mb="md">Admin Actions</Title>
              <Stack gap="md">
                <NumberInput
                  label="Commission Rate (%)"
                  value={commissionRate}
                  onChange={setCommissionRate}
                  min={0}
                  max={50}
                  step={0.1}
                  precision={1}
                />
                
                <Textarea
                  label="Verification Notes"
                  placeholder="Add notes about the verification process..."
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.currentTarget.value)}
                  minRows={3}
                />

                <Group grow>
                  <Button
                    color="green"
                    leftSection={<IconCheck size="1rem" />}
                    onClick={() => updateSellerStatus(selectedSeller.id, 'approved', true)}
                    loading={updating}
                    disabled={selectedSeller.seller_status === 'approved'}
                  >
                    Approve & Verify
                  </Button>
                  <Button
                    color="red"
                    leftSection={<IconX size="1rem" />}
                    onClick={() => updateSellerStatus(selectedSeller.id, 'rejected', false)}
                    loading={updating}
                  >
                    Reject
                  </Button>
                  <Button
                    color="orange"
                    onClick={() => updateSellerStatus(selectedSeller.id, 'suspended', false)}
                    loading={updating}
                  >
                    Suspend
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Stack>
        )}
      </Modal>

      {/* Full-size Image Modal */}
      <Modal
        opened={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        title={selectedImage?.title}
        size="xl"
        centered
      >
        {selectedImage && (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={selectedImage.url}
              alt={selectedImage.title}
              fit="contain"
              style={{ maxHeight: '70vh' }}
            />
            <Group justify="center" mt="md">
              <Button
                variant="light"
                leftSection={<IconDownload size="1rem" />}
                component="a"
                href={selectedImage.url}
                download
                target="_blank"
              >
                Download
              </Button>
            </Group>
          </div>
        )}
      </Modal>
    </Tabs.Panel>
  );
};

export default SellerVerification;